import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  CreditCard,
  CheckCircle2,
  Clock,
  DollarSign,
  ArrowRight,
  Loader2,
  Shield,
  Zap,
} from "lucide-react";

const features = [
  "Unlimited BIA Assessments",
  "Risk Assessment Engine",
  "AI-Powered Plan Generation",
  "ISO 22301 Compliance Tools",
  "Team Collaboration",
  "Audit Trail & Reporting",
  "Priority Support",
];

export default function BillingPage() {
  const { api, user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchSubscription();
    
    // Check if returning from Stripe checkout
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchSubscription = async () => {
    try {
      const response = await api.get("/subscriptions/current");
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Your subscription is now active.");
        await refreshUser();
        await fetchSubscription();
        setCheckingPayment(false);
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async () => {
    setProcessing(true);
    try {
      const response = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to create checkout session");
      toast.error(message);
      setProcessing(false);
    }
  };

  const isActive = subscription?.status === "active";

  return (
    <div className="space-y-8 animate-fade-in" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Billing & Subscription</h1>
        <p className="text-slate-600 mt-1">Manage your ResiliencyHUB subscription and billing.</p>
      </div>

      {/* Payment Processing Banner */}
      {checkingPayment && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <div>
                <p className="font-medium text-blue-900">Processing your payment...</p>
                <p className="text-sm text-blue-700">Please wait while we confirm your subscription.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Subscription Status */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Card data-testid="subscription-status-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Subscription Status</CardTitle>
              <Badge className={isActive ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                {isActive ? "SUBSCRIPTION ACTIVE" : subscription?.status?.toUpperCase() || "PENDING"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Current Rate</p>
                  <p className="font-semibold text-slate-900">
                    {subscription?.plan_rate === 0 ? "Included" : `$${subscription?.plan_rate}/month`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Next Billing Date</p>
                  <p className="font-semibold text-slate-900">
                    {subscription?.next_billing_date
                      ? new Date(subscription.next_billing_date).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-sky-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Access Level</p>
                  <p className="font-semibold text-slate-900">Full Access</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Free Access Info Card */}
      {!isActive && (
        <Card className="border-2 border-emerald-200 bg-emerald-50" data-testid="pricing-card">
          <CardContent className="p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
                  <Zap className="w-4 h-4" />
                  Free Access
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Full Platform Access</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-5xl font-bold text-emerald-600">Included</span>
                  <span className="text-slate-500">in your plan</span>
                </div>
                <p className="text-slate-600 max-w-md">
                  Enjoy full access to all ResiliencyHUB features including BIA engine, risk assessments, 
                  AI-powered plan generation, and compliance tools at no cost.
                </p>
              </div>

              <div className="flex-1 lg:max-w-sm">
                <ul className="space-y-3 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="p-4 bg-white rounded-lg border border-emerald-200 text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="font-semibold text-slate-900">You have full access!</p>
                  <p className="text-sm text-slate-600 mt-1">
                    All features are available on your current plan.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Subscription Features */}
      {isActive && (
        <Card data-testid="active-features-card">
          <CardHeader>
            <CardTitle className="text-lg">Your Plan Features</CardTitle>
            <CardDescription>Everything included in your subscription</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span className="text-slate-700">{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium text-slate-900 mb-1">What's included in my plan?</h4>
            <p className="text-sm text-slate-600">
              Your organization has full access to all ResiliencyHUB features per your assigned subscription plan, including BIA, Risk Assessment, AI-powered plan generation, and compliance tools.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-slate-900 mb-1">Will there be charges in the future?</h4>
            <p className="text-sm text-slate-600">
              We will notify all users well in advance before any pricing changes. Your data and configurations will be preserved.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-slate-900 mb-1">What features are included?</h4>
            <p className="text-sm text-slate-600">
              Full access to all BCM capabilities: BIA assessments, risk management, AI-powered recovery plans, compliance reporting, team collaboration, and ISO 22301 alignment tools.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
